import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

const STATUS_LABELS = {
  desired: 'Desired',
  possible: 'Possible',
  save_the_date: 'Save the Date',
  rsvped: "RSVP'd",
};

const STATUS_COLORS = {
  desired: '#818cf8',
  possible: '#fbbf24',
  save_the_date: '#f97316',
  rsvped: '#34d399',
};

const SIDE_COLORS = {
  Tim: '#60a5fa',
  Emilio: '#c084fc',
};

function Bar({ label, value, max, color, suffix }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="wd-bar-row">
      <span className="wd-bar-label">{label}</span>
      <div className="wd-bar-track">
        <div className="wd-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="wd-bar-value">{value}{suffix || ''}</span>
    </div>
  );
}

function DonutChart({ segments, centerLabel, centerValue, size }) {
  const s = size || 120;
  const r = (s - 12) / 2;
  const cx = s / 2;
  const cy = s / 2;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  let offset = 0;
  const arcs = segments.filter((seg) => seg.value > 0).map((seg) => {
    const pct = total > 0 ? seg.value / total : 0;
    const dashLength = pct * circumference;
    const dashOffset = -offset * circumference / total;
    const arc = { ...seg, dashLength, dashOffset, circumference };
    offset += seg.value;
    return arc;
  });

  return (
    <div className="wd-donut-container">
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#22232d" strokeWidth="10" />
        {arcs.map((arc, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={arc.color} strokeWidth="10"
            strokeDasharray={`${arc.dashLength} ${arc.circumference - arc.dashLength}`}
            strokeDashoffset={arc.dashOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="#e4e4e7" fontSize="20" fontWeight="700">
          {centerValue}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="#71717a" fontSize="10">
          {centerLabel}
        </text>
      </svg>
      <div className="wd-donut-legend">
        {segments.filter((s) => s.value > 0).map((seg, i) => (
          <span key={i} className="wd-legend-item">
            <span className="wd-legend-dot" style={{ background: seg.color }} />
            {seg.label}: {seg.value}
          </span>
        ))}
      </div>
    </div>
  );
}

function WeddingDashboard({ refreshKey }) {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/wedding-guests/counts');
      if (res.ok) setCounts(await res.json());
    } catch (err) {
      console.error('Failed to fetch wedding counts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [refreshKey, fetchData]);

  if (loading || !counts) {
    return (
      <section className="card">
        <h2>Wedding Dashboard</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  const statusSegments = Object.entries(STATUS_LABELS).map(([key, label]) => ({
    label,
    value: (counts.byStatusHeadcount && counts.byStatusHeadcount[key]) || 0,
    color: STATUS_COLORS[key],
  }));

  const activeHeadcount = counts.totalHeadcount - (counts.rsvpDeclinedHeadcount || 0);

  const rsvpSegments = [
    { label: 'Attending', value: counts.rsvpAttendingHeadcount || 0, color: '#34d399' },
    { label: 'Declined', value: counts.rsvpDeclinedHeadcount || 0, color: '#f87171' },
    { label: 'Pending', value: counts.totalHeadcount - (counts.rsvpAttendingHeadcount || 0) - (counts.rsvpDeclinedHeadcount || 0), color: '#3a3b4a' },
  ];

  const sideSegments = [
    { label: 'Tim', value: counts.timAttending || 0, color: SIDE_COLORS.Tim },
    { label: 'Emilio', value: counts.emilioAttending || 0, color: SIDE_COLORS.Emilio },
  ];

  const maxHeadcount = Math.max(counts.timAttending || 0, counts.emilioAttending || 0, 1);
  const maxStatusHc = Math.max(...Object.values(counts.byStatusHeadcount || {}), 1);

  return (
    <section className="card wd-section">
      <h2>Wedding Dashboard</h2>

      <div className="wd-stats-row">
        <div className="wd-stat-box">
          <span className="wd-stat-num">{counts.totalInvitations}</span>
          <span className="wd-stat-label">Invitations</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num" style={{ color: '#fbbf24' }}>{counts.totalAdditionalGuests || 0}</span>
          <span className="wd-stat-label">+1s</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num">{activeHeadcount}</span>
          <span className="wd-stat-label">Headcount</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num">{counts.invitesSent}</span>
          <span className="wd-stat-label">Invites Sent</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num" style={{ color: '#34d399' }}>{counts.rsvpAttendingHeadcount || 0}</span>
          <span className="wd-stat-label">Confirmed</span>
        </div>
      </div>

      <div className="wd-charts-row">
        <div className="wd-chart-col">
          <h3>By Status (headcount)</h3>
          <DonutChart segments={statusSegments} centerLabel="headcount" centerValue={activeHeadcount} />
        </div>
        <div className="wd-chart-col">
          <h3>RSVP Status</h3>
          <DonutChart segments={rsvpSegments} centerLabel="responses" centerValue={(counts.rsvpAttendingHeadcount || 0) + (counts.rsvpDeclined || 0)} />
        </div>
        <div className="wd-chart-col">
          <h3>By Side Attending</h3>
          <DonutChart segments={sideSegments} centerLabel="attending" centerValue={(counts.timAttending || 0) + (counts.emilioAttending || 0)} />
        </div>
      </div>

      <div className="wd-bars-section">
        <h3>Status Breakdown (headcount)</h3>
        {Object.entries(STATUS_LABELS).map(([key, label]) => {
          const hc = (counts.byStatusHeadcount && counts.byStatusHeadcount[key]) || 0;
          return <Bar key={key} label={label} value={hc} max={maxStatusHc} color={STATUS_COLORS[key]} />;
        })}
      </div>

      <div className="wd-bars-section">
        <h3>By Side Attending</h3>
        <Bar label="Tim" value={counts.timAttending || 0} max={maxHeadcount} color={SIDE_COLORS.Tim} />
        <Bar label="Emilio" value={counts.emilioAttending || 0} max={maxHeadcount} color={SIDE_COLORS.Emilio} />
      </div>

      <div className="wd-bars-section">
        <h3>Invite Progress</h3>
        <Bar label="Sent" value={counts.invitesSent} max={counts.totalInvitations || 1} color="#34d399"
          suffix={` / ${counts.totalInvitations}`} />
        <Bar label="RSVP'd" value={(counts.rsvpAttending || 0) + (counts.rsvpDeclined || 0)}
          max={counts.invitesSent || 1} color="#818cf8"
          suffix={` / ${counts.invitesSent}`} />
      </div>
    </section>
  );
}

export default WeddingDashboard;
